import { useRef, type ReactNode } from "react";
import { PillButton } from "./SitePrimitives";
import { HeroScene } from "./hero3d/HeroScene";
import type { SupportedLocale } from "../../lib/localization";

/**
 * Home hero: a blueprint drawing sheet with the 3D billboard street (see hero3d/), copy on the left
 * on desktop and in a note box over the bottom of the scene on smaller screens. A faint "terminal"
 * under the CTAs types commands that match the scene. The sheet's grid and a few of its lines run
 * on into the block below (the audiences), so the hero has no hard bottom edge.
 *
 * `image` (the CMS hero photo) is no longer shown; the scene replaces it.
 */
export function SiteHero({
  badge,
  badgeAvatars = [],
  title,
  description,
  primaryLabel,
  primaryHref,
  secondaryLabel,
  secondaryHref = "#services",
  locale = "en",
  onCta,
  children,
}: {
  badge: string;
  badgeAvatars?: string[];
  title: string;
  description: string;
  primaryLabel: string;
  primaryHref: string;
  secondaryLabel?: string;
  secondaryHref?: string;
  image?: string;
  locale?: SupportedLocale;
  onCta: (href: string, placement: string) => void;
  children?: ReactNode;
}) {
  const sheetRef = useRef<HTMLElement>(null);
  const termRef = useRef<HTMLDivElement>(null);
  return (
    <>
      <section id="hero" ref={sheetRef} className="hero-bp">
        <div className="hero-bp-frame" aria-hidden="true" />
        <div className="hero-bp-zones hero-bp-zones-top" aria-hidden="true">
          {["A", "B", "C", "D", "E", "F"].map((z) => (
            <span key={z}>{z}</span>
          ))}
        </div>
        <div className="hero-bp-zones hero-bp-zones-left" aria-hidden="true">
          {["1", "2", "3", "4"].map((z) => (
            <span key={z}>{z}</span>
          ))}
        </div>
        <HeroScene host={sheetRef} terminal={termRef} locale={locale === "es" ? "es" : "en"} />

        <div className="hero-bp-copy">
          {badge || badgeAvatars.length ? (
            <div className="hero-bp-badge">
              {badgeAvatars.length ? (
                <span className="flex">
                  {badgeAvatars.map((src) => (
                    <img key={src} src={src} alt="" className="h-[24px] w-[24px] rounded-full object-cover" />
                  ))}
                </span>
              ) : null}
              {badge ? <span className="site-badge">{badge}</span> : null}
            </div>
          ) : null}
          <h1 className="hero-bp-title">{title}</h1>
          {description ? <p className="hero-bp-lead">{description}</p> : null}
          <div className="hero-bp-actions">
            {primaryLabel ? (
              <PillButton href={primaryHref} arrow onClick={() => onCta(primaryHref, "hero-primary")}>
                {primaryLabel}
              </PillButton>
            ) : null}
            {secondaryLabel ? (
              <PillButton
                href={secondaryHref}
                variant="ghost"
                arrow
                onClick={() => onCta(secondaryHref, "hero-secondary")}
                className="hero-bp-ghost"
              >
                {secondaryLabel}
              </PillButton>
            ) : null}
          </div>
          <div className="hero-bp-term" aria-hidden="true" ref={termRef} />
        </div>
      </section>

      <div className="hero-bp-seam">
        <div className="hero-bp-bleed" aria-hidden="true">
          <svg viewBox="0 0 1200 320" preserveAspectRatio="xMidYMin slice">
            <line x1="1080" y1="-10" x2="1180" y2="90" strokeWidth=".8" opacity=".55" />
            <line x1="1040" y1="-10" x2="1290" y2="240" strokeWidth=".6" opacity=".25" strokeDasharray="6 6" />
            <line x1="120" y1="0" x2="120" y2="210" strokeWidth=".7" opacity=".4" />
            <path d="M114 60h12M114 120h12M114 180h12" strokeWidth=".7" opacity=".4" />
            <text x="132" y="64" opacity=".5">4</text>
            <line x1="640" y1="0" x2="640" y2="120" strokeWidth=".6" opacity=".22" strokeDasharray="2 5" />
          </svg>
        </div>
        {children ? <div className="relative pt-6 min-[810px]:pt-10">{children}</div> : null}
      </div>
    </>
  );
}

/** Faint blueprint marks for the background of later sections. Decorative only. */
export function BlueprintMark({ kind, className = "" }: { kind: "angle" | "cross" | "dimension" | "diagonal"; className?: string }) {
  if (kind === "angle")
    return (
      <svg className={`site-bp-mark ${className}`} viewBox="0 0 140 140" aria-hidden="true">
        <path d="M10 130H130M10 130L104 36" />
        <path d="M58 130A48 48 0 0 0 44 96" />
        <text x="64" y="120">45°</text>
      </svg>
    );
  if (kind === "cross")
    return (
      <svg className={`site-bp-mark ${className}`} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 0V24M0 12H24" />
        <circle cx="12" cy="12" r="6" />
      </svg>
    );
  if (kind === "dimension")
    return (
      <svg className={`site-bp-mark ${className}`} viewBox="0 0 260 30" aria-hidden="true">
        <path d="M0 15H260M0 8V22M260 8V22" />
      </svg>
    );
  return (
    <svg className={`site-bp-mark ${className}`} viewBox="0 0 200 200" aria-hidden="true">
      <path d="M0 200L200 0" strokeDasharray="4 6" />
    </svg>
  );
}
